class QuizSection extends HTMLElement {

  connectedCallback() {
    this.cacheElements();
    if (!this.totalSteps) return;
    this.bindEvents();
    this.restoreFromUrl() || this.updateProgress();
  }

  cacheElements() {
    this.currentStep = 0;
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.totalSteps = this.steps.length;
    this.selectedAnswers = new Array(this.totalSteps).fill(null);

    this.progress = {
      root: this.querySelector('.quiz__progress'),
      fill: this.querySelector('.quiz__progress-fill'),
      current: this.querySelector('[data-quiz-current]'),
      label: this.querySelector('.quiz__progress-label'),
    };

    this.results = {
      root: this.querySelector('[data-quiz-results]'),
      bestMatch: this.querySelector('[data-quiz-best-match]'),
      altMatch: this.querySelector('[data-quiz-alt-match]'),
      altCol: this.querySelector('.quiz__results-alt'),
      divider: this.querySelector('.quiz__results-divider'),
      columns: this.querySelector('.quiz__results-columns'),
      empty: this.querySelector('.quiz__results-empty'),
    };

    this.pool = this.querySelector('.quiz__pool');
  }

  bindEvents() {
    const actions = [
      ['.quiz__answer', (el) => this.selectAnswer(el)],
      ['[data-quiz-back]', () => this.prevStep()],
      ['[data-quiz-prev]', () => this.prevStep()],
      ['[data-quiz-next]', (el) => { if (!el.disabled) this.nextStep(); }],
      ['[data-quiz-results-back]', () => this.goBackFromResults()],
    ];

    this.addEventListener('click', (e) => {
      for (const [selector, handler] of actions) {
        const target = e.target.closest(selector);
        if (target) { handler(target); return; }
      }
    });
  }

  selectAnswer(btn) {
    const step = btn.closest('[data-step]');
    if (!step) return;

    const stepIndex = parseInt(step.dataset.step, 10);

    step.querySelectorAll('.quiz__answer').forEach(a => a.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    this.selectedAnswers[stepIndex] = btn;

    const nextBtn = step.querySelector('[data-quiz-next]');
    if (nextBtn) nextBtn.disabled = false;
  }

  nextStep() {
    if (this.currentStep < this.totalSteps - 1) {
      this.goToStep(this.currentStep + 1);
    } else {
      this.showResults();
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  goToStep(index) {
    this.steps[this.currentStep].hidden = true;
    this.currentStep = index;
    this.steps[this.currentStep].hidden = false;
    this.updateProgress();
    this.updateUrl();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateProgress() {
    const { fill, current } = this.progress;
    if (!fill || !current) return;

    const pct = ((this.currentStep + 1) / this.totalSteps) * 100;
    fill.style.width = pct + '%';
    current.textContent = this.currentStep + 1;
  }

  setProgressComplete() {
    const { fill, label, current, root } = this.progress;
    if (fill) fill.style.width = '100%';
    if (label) label.textContent = this.dataset.resultLabel || 'Result';
    if (current) current.parentElement.hidden = true;
    if (root) root.classList.add('is-complete');
  }

  resetProgress() {
    const { label, current, root } = this.progress;
    if (label) label.textContent = this.dataset.progressLabel || 'Progress:\u00a0';
    if (current) current.parentElement.hidden = false;
    if (root) root.classList.remove('is-complete');
  }

  showResults(skipScroll) {
    this.steps[this.currentStep].hidden = true;
    this.setProgressComplete();

    const topProducts = this.calculateTopProducts();
    this.renderResults(topProducts);

    if (this.results.root) this.results.root.hidden = false;
    this.updateUrl(true);
    if (!skipScroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  goBackFromResults() {
    const { root, bestMatch, altMatch } = this.results;
    if (root) root.hidden = true;
    if (bestMatch) bestMatch.innerHTML = '';
    if (altMatch) altMatch.innerHTML = '';

    this.resetProgress();
    this.currentStep = this.totalSteps - 1;
    this.steps[this.currentStep].hidden = false;
    this.updateProgress();
    this.updateUrl();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  calculateTopProducts() {
    const frequency = {};

    this.selectedAnswers.forEach(btn => {
      if (!btn) return;
      const raw = btn.dataset.products;
      if (!raw) return;
      raw.split(',').forEach(handle => {
        const h = handle.trim();
        if (h) frequency[h] = (frequency[h] || 0) + 1;
      });
    });

    return Object.entries(frequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2);
  }

  renderResults(topProducts) {
    const { bestMatch, altMatch, divider, altCol, columns, empty } = this.results;

    if (bestMatch) bestMatch.innerHTML = '';
    if (altMatch) altMatch.innerHTML = '';

    if (topProducts.length === 0) {
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;

    this.placeProductCard(topProducts[0]?.[0], bestMatch);
    this.placeProductCard(topProducts[1]?.[0], altMatch);

    const hasBest = bestMatch && bestMatch.children.length > 0;
    const hasAlt = altMatch && altMatch.children.length > 0;
    const isSingle = hasBest && !hasAlt;
    if (divider) divider.hidden = isSingle;
    if (altCol) altCol.hidden = isSingle;
    if (columns) columns.classList.toggle('is-single', isSingle);
  }

  placeProductCard(handle, container) {
    if (!handle || !container || !this.pool) return;
    const card = this.pool.querySelector('[data-product-handle="' + handle + '"]');
    if (card) container.appendChild(card.cloneNode(true));
  }

  getAnswerIndices() {
    return this.selectedAnswers.map((btn, i) => {
      if (!btn) return '';
      const answers = Array.from(this.steps[i].querySelectorAll('.quiz__answer'));
      return answers.indexOf(btn);
    });
  }

  updateUrl(showResults) {
    const params = new URLSearchParams(window.location.search);
    const indices = this.getAnswerIndices();
    const hasAnswers = indices.some(v => v !== '');

    hasAnswers ? params.set('qa', indices.join(',')) : params.delete('qa');
    showResults ? params.set('qr', '1') : params.delete('qr');

    const qs = params.toString();
    const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    history.replaceState(null, '', url);
  }

  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const qa = params.get('qa');
    if (!qa) return false;

    const restored = this.restoreAnswers(qa.split(','));
    if (!restored) return false;

    if (params.get('qr') === '1' && this.selectedAnswers.every(a => a !== null)) {
      this.showResults(true);
      return true;
    }

    this.restoreStep();
    this.updateProgress();
    return true;
  }

  restoreAnswers(indices) {
    let restored = false;

    indices.forEach((val, stepIndex) => {
      if (val === '' || stepIndex >= this.totalSteps) return;
      const answerIndex = parseInt(val, 10);
      if (isNaN(answerIndex)) return;

      const step = this.steps[stepIndex];
      const btn = step.querySelectorAll('.quiz__answer')[answerIndex];
      if (!btn) return;

      step.querySelectorAll('.quiz__answer').forEach(a => a.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      this.selectedAnswers[stepIndex] = btn;

      const nextBtn = step.querySelector('[data-quiz-next]');
      if (nextBtn) nextBtn.disabled = false;
      restored = true;
    });

    return restored;
  }

  restoreStep() {
    const lastAnswered = this.selectedAnswers.reduce((last, a, i) => a ? i : last, 0);
    const targetStep = Math.min(lastAnswered + 1, this.totalSteps - 1);

    if (targetStep > 0) {
      this.steps[0].hidden = true;
      this.currentStep = targetStep;
      this.steps[this.currentStep].hidden = false;
    }
  }
}

if (!customElements.get('quiz-section')) {
  customElements.define('quiz-section', QuizSection);
}
